import { FC, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { FormDialog } from 'components';
import {
  DailyNavPriceAdjustmentTiming,
  DailyNavRecordWithDay,
  DailyNavReviewNote,
  DailyNavUpdatePriceAdjustmentTiming,
  isFundManaged,
  TIMING_READ_TO_UPDATE,
} from 'pages/Intelligence/DailyNav/daily-nav.api.types';
import {
  isDailyNavReviewComplete,
  missingDailyNavReviewFields,
  REVIEW_PRICE_LABEL,
} from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { useUpdateDailyNavRecord } from 'services/hooks/firms';
import {
  useCreateDailyNavNote,
  useDeleteDailyNavNote,
  useDownloadDailyNavNoteAttachment,
} from 'services/hooks/firms/useDailyNavReport';
import { ReviewPriceDialogProps, ReviewPriceFormValues } from './ReviewPriceDialog.props';
import { reviewPriceDialogColors as colors } from './ReviewPriceDialog.styles';
import ReviewPriceDialogContent from './ReviewPriceDialogContent';

const mapTiming = (
  timing: DailyNavPriceAdjustmentTiming | null | undefined
): DailyNavUpdatePriceAdjustmentTiming | null => (timing ? TIMING_READ_TO_UPDATE[timing] : null);

const getFormValuesFromRow = (row: ReviewPriceDialogProps['row']): ReviewPriceFormValues => ({
  reviewed_by_scalar: row?.record.reviewed_by_scalar ?? false,
  discussed_with_fund: row?.record.discussed_with_fund ?? false,
  price_adjusted: (row?.record as DailyNavRecordWithDay | undefined)?.price_adjusted ?? null,
  // Mapped, not cast: read and update generate separate enums.
  price_adjustment_timing: mapTiming((row?.record as DailyNavRecordWithDay | undefined)?.price_adjustment_timing),
});

const notesFromRow = (row: ReviewPriceDialogProps['row']): DailyNavReviewNote[] =>
  ((row?.record as DailyNavRecordWithDay | undefined)?.notes ?? []).filter(
    note => Boolean(note.body?.trim()) || Boolean(note.attachments?.length)
  );

const FINALIZED_LOCK_REASON = 'This Daily NAV day is finalized. Review status is locked; notes can still be added.';
const CLOSED_LOCK_REASON = 'This Daily NAV day is closed. Only notes and documents can be added.';

const ReviewPriceDialog: FC<ReviewPriceDialogProps> = ({ row, open, isLocked = false, onClose }) => {
  const day = (row?.record as DailyNavRecordWithDay | undefined)?.day;
  const isFinalized = Boolean(day?.is_finalized ?? row?.record.finalized_at);
  // Review answers are read-only once the day is finalized or closed.
  const reviewFieldsLocked = isFinalized || isLocked || Boolean(row?.record.is_locked);
  let lockedReason: string | undefined;
  if (isFinalized) {
    lockedReason = FINALIZED_LOCK_REASON;
  } else if (reviewFieldsLocked) {
    lockedReason = CLOSED_LOCK_REASON;
  }
  // The row's own anchor, so the hint describes the rule it was reviewed under.
  const fundManaged = isFundManaged(row?.record as DailyNavRecordWithDay | undefined);

  const [formValues, setFormValues] = useState<ReviewPriceFormValues>(() => getFormValuesFromRow(row));
  const [notes, setNotes] = useState<DailyNavReviewNote[]>(() => notesFromRow(row));
  const [noteDraft, setNoteDraft] = useState('');
  const [noteFiles, setNoteFiles] = useState<File[]>([]);
  const { mutateAsync: updateDailyNavRecord, isPending } = useUpdateDailyNavRecord();
  const { mutateAsync: createNote, isPending: isCreatingNote } = useCreateDailyNavNote();
  const { mutateAsync: deleteNote, isPending: isDeletingNote } = useDeleteDailyNavNote();
  const { mutate: downloadAttachment } = useDownloadDailyNavNoteAttachment();

  useEffect(() => {
    setFormValues(getFormValuesFromRow(row));
    setNotes(notesFromRow(row));
    setNoteDraft('');
    setNoteFiles([]);
  }, [row]);

  const valuationFinalizedToday = Boolean(
    (row?.record as DailyNavRecordWithDay | undefined)?.valuation_finalized_today
  );
  // Saving next trading day here would file the new valuation on the report
  // and leave Today's Price on the previous one.
  const nextDayInvalid =
    !reviewFieldsLocked &&
    valuationFinalizedToday &&
    formValues.price_adjusted === true &&
    formValues.price_adjustment_timing === 'next_day';

  const hasPendingNote = Boolean(noteDraft.trim()) || noteFiles.length > 0;
  const pendingNoteCount = notes.length + (hasPendingNote ? 1 : 0);
  const willBeComplete = isDailyNavReviewComplete({
    ...formValues,
    noteCount: pendingNoteCount,
    fundManaged,
  });
  const missing = missingDailyNavReviewFields({
    ...formValues,
    noteCount: pendingNoteCount,
    fundManaged,
  });
  const saveLabel = willBeComplete ? 'Save review' : 'In Progress';

  const persistDraftNote = async (recordId: number) => {
    if (!hasPendingNote) {
      return null;
    }
    const note = await createNote({ recordId, body: noteDraft.trim(), files: noteFiles });
    setNoteDraft('');
    setNoteFiles([]);
    return note;
  };

  const handleAddNote = async () => {
    if (!row) {
      return;
    }
    const note = await persistDraftNote(row.record.id as number);
    if (note) {
      setNotes([...notes, note]);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!row) {
      return;
    }
    await deleteNote({ recordId: row.record.id as number, noteId }); // noteId is the persisted id
    setNotes(notes.filter(note => note.id !== noteId));
  };

  const handleSave = async () => {
    if (!row) {
      return;
    }
    try {
      if (!reviewFieldsLocked) {
        await updateDailyNavRecord({
          id: row.record.id as number,
          data: {
            reviewed_by_scalar: formValues.reviewed_by_scalar,
            discussed_with_fund: formValues.discussed_with_fund,
            price_adjusted: formValues.price_adjusted,
            price_adjustment_timing: formValues.price_adjustment_timing,
          },
        });
      }
      await persistDraftNote(row.record.id as number);
      onClose();
    } catch {
      // Keep the dialog open; the mutation's onError already surfaced a snackbar.
    }
  };

  const isBusy = isPending || isCreatingNote || isDeletingNote;

  return (
    <FormDialog
      title={REVIEW_PRICE_LABEL}
      open={open}
      editMode
      isValid
      isLoading={isBusy}
      showSaveButton={false}
      onClose={onClose}
      customCancelButton={
        <Button
          id="review-price-cancel-button"
          onClick={onClose}
          disabled={isBusy && !reviewFieldsLocked}
          sx={{
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            color: colors.slate700,
            fontSize: 12,
            paddingX: 1.5,
            paddingY: 0.5,
            textTransform: 'none',
          }}>
          {reviewFieldsLocked ? 'Close' : 'Cancel'}
        </Button>
      }
      customButton={
        <Button
          id="review-price-save-button"
          onClick={handleSave}
          disabled={isBusy || nextDayInvalid}
          sx={{
            backgroundColor: colors.navy,
            borderRadius: '4px',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            paddingX: 1.5,
            paddingY: 0.5,
            textTransform: 'none',
            '&:hover': { backgroundColor: colors.navyHover },
          }}>
          {saveLabel}
        </Button>
      }>
      <Box>
        <ReviewPriceDialogContent
          row={row}
          formValues={formValues}
          setFormValues={setFormValues}
          notes={notes}
          noteDraft={noteDraft}
          setNoteDraft={setNoteDraft}
          noteFiles={noteFiles}
          setNoteFiles={setNoteFiles}
          onAddNote={handleAddNote}
          // Removing a note rewrites the day's history, so it closes with it.
          onDeleteNote={reviewFieldsLocked ? undefined : handleDeleteNote}
          onDownloadAttachment={downloadAttachment}
          reviewFieldsLocked={reviewFieldsLocked}
          lockedReason={lockedReason}
          notesLocked={false}
        />
        {!willBeComplete && (
          <Typography
            sx={{ color: colors.slate500, fontSize: 11, marginTop: 1 }}
            data-testid="review-price-missing-hint">
            Still needed: {missing.join(', ')}.
          </Typography>
        )}
      </Box>
    </FormDialog>
  );
};

export default ReviewPriceDialog;
